import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { WizardContext } from "./wizard-state";
import { DEFAULT_WIZARD_DATA, evaluateAdminStatus, evaluateClinicalStatus, validateClose, buildEvaluationDto } from "./rules";
import { Step1Basicos } from "./steps/Step1Basicos";
import { Step2Administrativo } from "./steps/Step2Administrativo";
import { Step3Clinico } from "./steps/Step3Clinico";
import { Step4Resultado } from "./steps/Step4Resultado";
import { useCreateEvaluation, useUpdateEvaluation, useEvaluations } from "./queries";
import { useAuth } from "@features/auth/AuthContext";
import { HttpError } from "@shared/http/client";
import type { WizardData } from "@/types/wizard";
import type { EvaluationEnvelope } from "@/types/evaluation";

const STEP_NAMES = ["1. Paciente", "2. Administrativo", "3. Clínico", "4. Resultado"];

const DRAFT_KEY = "foca-wizard-draft";
type Draft = { data: WizardData; step: number; savedAt: number; userId?: number };

function loadDraft(userId?: number): Draft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Draft;
    if (d.userId && userId && d.userId !== userId) return null;
    return d;
  } catch { return null; }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_KEY);
}

function validateStep(step: number, d: WizardData): Set<string> {
  const errors = new Set<string>();
  if (step === 1) {
    (["doc", "nombre", "cirugia", "fecha", "telefono", "programador", "diagnostico"] as const).forEach((k) => {
      if (!d[k].trim()) errors.add(k);
    });
  }
  if (step === 3) {
    (["ps", "pd", "fc", "saturacion", "temp", "glicemia", "antecedentes", "medicamentos", "alergias", "cirugiasPrevias"] as const).forEach((k) => {
      if (!d[k].trim()) errors.add(k);
    });
    if (!d.blefaritis) errors.add("blefaritis");
  }
  return errors;
}

/** Merge una valoración cargada del server sobre los defaults del wizard.
 *  Los campos nested (administrativos, clinicos, etc.) se aplanan al shape plano de WizardData. */
function evaluationToWizardData(e: EvaluationEnvelope): WizardData {
  const nested = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
  const adm = nested(e.administrativos);
  const clin = nested(e.clinicos);
  const ant = nested(e.antecedentes_detallados);
  const chk = nested(e.chequeo);
  const gest = nested(e.gestionEvaluador);
  const str = (v: unknown, dflt = ""): string => (v == null ? dflt : String(v));
  const bool = (v: unknown): boolean => v === true || v === "true";

  return {
    ...DEFAULT_WIZARD_DATA,
    doc: str(e.doc), nombre: str(e.nombre),
    edad: str(e.edad), eps: str(e.eps),
    medicoTratante: str(e.medicoTratante), tipoAnestesia: str(e.tipoAnestesia),
    cirugia: str(e.cirugia), fecha: str(e.fecha),
    telefono: str(e.telefono), programador: str(e.programador),
    diagnostico: str(e.diagnostico), cie10: str(e.cie10),
    diagnosticosExtra: Array.isArray((e as { diagnosticosExtra?: unknown }).diagnosticosExtra)
      ? ((e as { diagnosticosExtra: unknown[] }).diagnosticosExtra
          .map((dx) => {
            const o = (dx && typeof dx === "object") ? (dx as Record<string, unknown>) : {};
            return { code: String(o.code || ""), description: String(o.description || "") };
          })
          .filter((dx) => dx.code || dx.description))
      : [],
    observaciones: str(e.observaciones),
    antecedentes: str(e.antecedentes), medicamentos: str(e.medicamentos),
    alergias: str(e.alergias), cirugiasPrevias: str(e.cirugiasPrevias),
    orden: (str(adm.orden, "si") as WizardData["orden"]),
    copago: (str(adm.copago, "si") as WizardData["copago"]),
    insumo: (str(adm.insumo, "si") as WizardData["insumo"]),
    insumoDetalle: str(adm.insumoDetalle),
    autorizacion: (str(adm.autorizacion, "si") as WizardData["autorizacion"]),
    acompanante: (str(adm.acompanante, "si") as WizardData["acompanante"]),
    esMenorEdad: bool(adm.esMenorEdad),
    nombreAcompanante: str(adm.nombreAcompanante),
    ayuno: (str(adm.ayuno, "si") as WizardData["ayuno"]),
    suspensionMedConfirm: (str(adm.suspensionMedConfirm, "si") as WizardData["suspensionMedConfirm"]),
    dispPostop: (str(adm.dispPostop, "si") as WizardData["dispPostop"]),
    recMedAntihipertensivos: bool(adm.recMedAntihipertensivos),
    recMedTiroidea: bool(adm.recMedTiroidea),
    ps: str(clin.ps), pd: str(clin.pd),
    paControlada: (str(clin.paControlada, "si") as WizardData["paControlada"]),
    fc: str(clin.fc), saturacion: str(clin.saturacion), temp: str(clin.temp),
    glicemia: str(clin.glicemia),
    glicemiaControlada: (str(clin.glicemiaControlada, "si") as WizardData["glicemiaControlada"]),
    peso: str(clin.peso),
    antDiabetes: bool(ant.diabetes), antHipertension: bool(ant.hipertension),
    antTiroidea: bool(ant.tiroidea), antRenal: bool(ant.renal), antGripa: bool(ant.gripa),
    antCardio: bool(ant.cardio), antCardioCual: str(ant.cardio_cual),
    antPulmonar: bool(ant.pulmonar), antPulmonarCual: str(ant.pulmonar_cual),
    antACV: bool(ant.acv), antInfartos: bool(ant.infartos),
    antTromboembolismo: bool(ant.tromboembolismo),
    antRetrasoMental: bool(ant.retraso_mental), antDown: bool(ant.down),
    antColostomia: bool(ant.colostomia), antCrisisAnsiosa: bool(ant.crisis_ansiosa),
    antOtra: str(ant.otra),
    anticoagulante: (str(ant.anticoagulante, "ninguno") as WizardData["anticoagulante"]),
    anticoagulanteOtro: str(ant.anticoagulante_otro),
    aspirinaSuspendida: (str(ant.aspirina_suspendida, "na") as WizardData["aspirinaSuspendida"]),
    autorizAnestAnticoag: (str(ant.autoriz_anest_anticoag, "na") as WizardData["autorizAnestAnticoag"]),
    suspPreviasDescontrol: bool(ant.susp_previas_descontrol),
    suspNoAutorizadaMedEsenciales: bool(ant.susp_no_autorizada_med_esenciales),
    blefaritis: (str(clin.blefaritis, "ninguna") as WizardData["blefaritis"]),
    resp: (str(clin.resp, "ninguno") as WizardData["resp"]),
    piel: (str(clin.piel, "ninguna") as WizardData["piel"]),
    dental: (str(clin.dental, "ninguno") as WizardData["dental"]),
    conjuntivitis: (str(clin.conjuntivitis, "ninguna") as WizardData["conjuntivitis"]),
    estadoHigienico: (str(clin.estadoHigienico, "adecuado") as WizardData["estadoHigienico"]),
    modalidad: (str(chk.modalidad, "presencial") as WizardData["modalidad"]),
    orientComprende: (str(chk.orientComprende, "si") as WizardData["orientComprende"]),
    orientHoraAntic: (str(chk.orientHoraAntic, "si") as WizardData["orientHoraAntic"]),
    orientSignosAlarma: (str(chk.orientSignosAlarma, "si") as WizardData["orientSignosAlarma"]),
    requiereGestion: (str(gest.requiereGestion, "no") as WizardData["requiereGestion"]),
    tipoGestion: (str(gest.tipoGestion, "") as WizardData["tipoGestion"]),
    accionesEvaluador: str(gest.accionesEvaluador),
    profesionalConsultado: str(gest.profesionalConsultado),
    respuestaRecibida: str(gest.respuestaRecibida),
    evidenciaCierre: str(gest.evidenciaCierre),
    decisionFinalEvaluador: (str(e.accionFinal, "") as WizardData["decisionFinalEvaluador"]),
  };
}

export function Wizard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const initialDoc = searchParams.get("doc") || "";
  const viewId = searchParams.get("id");
  const editMode = searchParams.get("edit") === "1";
  // Solo readonly cuando se abre por id SIN modo edit
  const readonly = !!viewId && !editMode;

  // Cargar valoración por id si es modo readonly
  const { data: allEvals, isLoading: loadingEvals } = useEvaluations({});
  const targetEval = useMemo(() => {
    if (!viewId || !allEvals) return null;
    const idNum = Number(viewId);
    return allEvals.items.find((e) => Number(e.id) === idNum) || null;
  }, [viewId, allEvals]);

  const existingDraft = useMemo(() => (readonly ? null : loadDraft(user?.id)), [user?.id, readonly]);
  const [draftDecision, setDraftDecision] = useState<"pending" | "resumed" | "discarded">(
    !readonly && existingDraft && !initialDoc ? "pending" : "discarded"
  );

  const [data, setData] = useState<WizardData>(() => ({
    ...DEFAULT_WIZARD_DATA,
    doc: initialDoc,
    programador: user?.name || user?.username || "",
  }));
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [closeError, setCloseError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved">("idle");
  const [autoSavedAt, setAutoSavedAt] = useState<number | null>(null);

  const createMut = useCreateEvaluation();
  const updateMut = useUpdateEvaluation();
  const dirtyRef = useRef(false);

  // Cargar datos del readonly O edit cuando llegan
  useEffect(() => {
    if (viewId && targetEval) {
      setData(evaluationToWizardData(targetEval));
      // Solo saltar al último paso en readonly; en edit dejar Step 1 para revisar
      if (readonly) setStep(4);
    }
  }, [viewId, targetEval, readonly]);

  // Autoguardado (solo modo edición)
  useEffect(() => {
    if (readonly) return;
    if (draftDecision === "pending") return;
    if (saveStatus === "saved") return;
    const id = setInterval(() => {
      if (!dirtyRef.current) return;
      const draft: Draft = { data, step, savedAt: Date.now(), userId: user?.id };
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      setAutoSavedAt(draft.savedAt);
      dirtyRef.current = false;
    }, 5000);
    return () => clearInterval(id);
  }, [data, step, user?.id, draftDecision, saveStatus, readonly]);

  const set = useCallback(<K extends keyof WizardData>(key: K, value: WizardData[K]) => {
    if (readonly) return;
    setData((prev) => ({ ...prev, [key]: value }));
    dirtyRef.current = true;
    setErrors((prev) => {
      if (!prev.has(key as string)) return prev;
      const next = new Set(prev);
      next.delete(key as string);
      return next;
    });
    setCloseError(null);
  }, [readonly]);

  const reset = useCallback(() => {
    setData({ ...DEFAULT_WIZARD_DATA, programador: user?.name || user?.username || "" });
    setStep(1);
    setErrors(new Set());
    setCloseError(null);
    setSaveStatus("idle");
    setAutoSavedAt(null);
    dirtyRef.current = false;
    clearDraft();
  }, [user]);

  const resumeDraft = () => {
    if (!existingDraft) return;
    setData(existingDraft.data);
    setStep(existingDraft.step);
    setAutoSavedAt(existingDraft.savedAt);
    setDraftDecision("resumed");
  };

  const discardDraft = () => {
    clearDraft();
    setDraftDecision("discarded");
  };

  const ctxValue = useMemo(() => ({ data, set, reset, readonly }), [data, set, reset, readonly]);

  const goTo = (target: number) => {
    if (readonly) { setStep(target); return; }
    if (target <= step) {
      setStep(target);
      setErrors(new Set());
      return;
    }
    for (let s = step; s < target; s++) {
      const stepErrors = validateStep(s, data);
      if (stepErrors.size > 0) {
        setStep(s);
        setErrors(stepErrors);
        return;
      }
    }
    setStep(target);
    setErrors(new Set());
  };

  const onSave = async () => {
    const admin = evaluateAdminStatus(data);
    const clinical = evaluateClinicalStatus(data);
    const err = validateClose(data, admin, clinical);
    if (err) { setCloseError(err); return; }

    try {
      const dto = buildEvaluationDto(data, admin, clinical, data.decisionFinalEvaluador);
      if (editMode && viewId) {
        await updateMut.mutateAsync({ id: Number(viewId), evaluation: dto });
      } else {
        await createMut.mutateAsync(dto);
        clearDraft();
      }
      setSaveStatus("saved");
    } catch (e) {
      setCloseError(e instanceof HttpError ? e.message : "Error guardando la valoración.");
    }
  };

  if (readonly && loadingEvals) {
    return <div className="p-24 text-muted">Cargando valoración…</div>;
  }

  if (readonly && !targetEval && !loadingEvals) {
    return (
      <div className="p-24">
        <div className="alert-inline alert-inline--danger alert-inline--md">
          Valoración id={viewId} no encontrada. Vuelve al <a href="/historial" className="link-brand" onClick={(e) => { e.preventDefault(); navigate("/historial"); }}>historial</a>.
        </div>
      </div>
    );
  }

  if (draftDecision === "pending" && existingDraft) {
    const minutesAgo = Math.max(1, Math.round((Date.now() - existingDraft.savedAt) / 60000));
    return (
      <div className="p-24">
        <div className="wizard-section">
          <h3 className="wizard-section-title">Tienes una valoración a medio llenar</h3>
          <p className="text-muted">
            Encontramos una valoración de <b>{existingDraft.data.nombre || existingDraft.data.doc || "paciente sin nombre"}</b>
            {" "}que dejaste hace ~{minutesAgo} min en el paso {existingDraft.step}.
          </p>
          <div className="mt-16" style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn btn-primary" onClick={resumeDraft}>
              Retomar donde la dejé
            </button>
            <button type="button" className="btn btn-secondary" onClick={discardDraft}>
              Descartar y empezar nueva
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (saveStatus === "saved") {
    return (
      <div className="p-24">
        <div className="alert-inline alert-inline--success alert-inline--md alert-inline--bold">
          ✅ Valoración guardada correctamente para {data.nombre} (doc {data.doc}).
        </div>
        <div className="mt-16" style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn btn-primary" onClick={reset}>
            Nueva valoración
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/historial")}>
            Ver historial
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/pendientes")}>
            Ver pendientes
          </button>
        </div>
      </div>
    );
  }

  return (
    <WizardContext.Provider value={ctxValue}>
      <fieldset disabled={readonly} className="wizard-fieldset">
        <div className="p-24">
          <div className="wizard-banner">
            <div className="wizard-banner-title">
              {readonly && <span className="badge badge-blue mr-8">👁️ Solo lectura</span>}
              {editMode && <span className="badge badge-warn mr-8">✏️ Editando</span>}
              {data.nombre || "Nuevo registro"}
            </div>
            <div className="wizard-banner-meta">
              {[data.doc, data.cirugia, data.fecha].filter(Boolean).join(" · ") ||
                "Complete los datos del paciente"}
            </div>
          </div>

          <div className="wizard-stepper mt-16">
            {STEP_NAMES.map((name, i) => {
              const n = i + 1;
              return (
                <button
                  key={n}
                  type="button"
                  className={`wizard-step ${n === step ? "active" : ""} ${n < step ? "done" : ""}`}
                  onClick={() => goTo(n)}
                >
                  {name}
                </button>
              );
            })}
          </div>

          {!readonly && autoSavedAt && (
            <div className="wizard-autosave mt-8">
              💾 Borrador guardado automáticamente {new Date(autoSavedAt).toLocaleTimeString()}
            </div>
          )}

          {errors.size > 0 && (
            <div className="alert-inline alert-inline--danger alert-inline--md mt-16">
              ⚠️ Completa los campos marcados en rojo antes de continuar.
            </div>
          )}

          <div className="wizard-body mt-16">
            {step === 1 && <Step1Basicos errors={errors} />}
            {step === 2 && <Step2Administrativo />}
            {step === 3 && <Step3Clinico errors={errors} />}
            {step === 4 && <Step4Resultado closeError={closeError} />}
          </div>

          <div className="wizard-actions mt-24">
            {step > 1 && (
              <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)} disabled={false}>
                ← Anterior
              </button>
            )}
            {step < 4 && (
              <button type="button" className="btn btn-primary" onClick={() => goTo(step + 1)} disabled={false}>
                Siguiente →
              </button>
            )}
            {!readonly && step === 4 && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
                disabled={createMut.isPending || updateMut.isPending}
              >
                {(createMut.isPending || updateMut.isPending)
                  ? "Guardando…"
                  : editMode ? "Guardar cambios" : "Guardar valoración"}
              </button>
            )}
            {readonly && step === 4 && (
              <button type="button" className="btn btn-secondary" onClick={() => navigate("/historial")}>
                Volver al historial
              </button>
            )}
          </div>
        </div>
      </fieldset>
    </WizardContext.Provider>
  );
}
